import React from 'react';

import { EntryPointProps } from 'react-relay';

import PageIcon from '@atlaskit/icon/core/page';
import { EMBEDDED_CONFLUENCE_MODE, Page, Whiteboard } from '@atlassian/embedded-confluence';
import {
	PreviewPanel,
	PreviewPanelBody,
	PreviewPanelHeader,
	PreviewPanelTitle,
} from '@atlassian/preview-panel';
import { useGetPreviewPanelsActions } from '@atlassian/preview-panels-api';

import { PARENT_PRODUCTS } from '../../common/constants';
import { getWhiteboardUrlMatch } from '../../utils/whiteboard';

import { onEditMode } from './utils';

export interface ConfluencePagePreviewPanelProps {
	title: string;
	url: string;
	parentProduct: (typeof PARENT_PRODUCTS)[keyof typeof PARENT_PRODUCTS];
}

export type ConfluencePagePreviewPanelEntrypointProps = EntryPointProps<
	Record<string, never>,
	Record<string, never>,
	ConfluencePagePreviewPanelProps,
	Record<string, never>
>;

const ConfluencePagePreviewPanel = ({
	props: { title, url, parentProduct },
}: ConfluencePagePreviewPanelEntrypointProps) => {
	const { close, setPanelWidth } = useGetPreviewPanelsActions();

	const onClose = () => {
		close();
	};

	const onModeChange = (mode: EMBEDDED_CONFLUENCE_MODE) => onEditMode(mode, setPanelWidth);

	const whiteboardMatch = getWhiteboardUrlMatch(url);
	const content = whiteboardMatch ? (
		<Whiteboard
			contentId={whiteboardMatch.params.contentId}
			spaceKey={whiteboardMatch.params.spaceKey}
			parentProduct={parentProduct}
		/>
	) : (
		<Page
			url={url}
			showFooter={false}
			hasFooterLogo={false}
			onModeChange={onModeChange}
			parentProduct={parentProduct}
		/>
	);

	return (
		<PreviewPanel testId="confluence-preview-panel">
			<PreviewPanelHeader onClose={onClose} url={url}>
				<PreviewPanelTitle title={title} Icon={PageIcon} />
			</PreviewPanelHeader>
			<PreviewPanelBody>{content}</PreviewPanelBody>
		</PreviewPanel>
	);
};

export default ConfluencePagePreviewPanel;
